import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { useFormik } from 'formik';

// Store
import { Dispatch } from '../../store/store';

// Constants
import ROUTE from '../../constants/route-constants';
import { TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';
import { CreateLocationFormValues } from './types';
import Switcher, { ResponseSwitch } from '../../components/Switcher/Switcher';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import CycleMeasurement from '../../enums/cycle-measurement';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import DraftMetaFields from '../../components/DraftMetaFields/DraftMetaFields';
import { useTranslation } from 'react-i18next';

type CreateLocationProps = ReturnType<typeof mapDispatch>;

const CreateLocation: React.FC<CreateLocationProps> = ({ createLocation, getOrganizationUnits }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const formik = useFormik<CreateLocationFormValues>({
    initialValues: {
      name: '',
      city: '',
      isCelsius: false,
      metaFields: [],
    },
    validationSchema: object({
      name: string()
        .required(t('clients.form.validation.name.required'))
        .min(
          TEXT_LENGTH_LIMITS.MIN,
          t('clients.form.validation.name.minLength', '', { min: TEXT_LENGTH_LIMITS.MIN }),
        )
        .max(
          TEXT_LENGTH_LIMITS.MAX,
          t('clients.form.validation.name.maxLength', '', { max: TEXT_LENGTH_LIMITS.MAX }),
        ),
      city: string()
        .required(t('clients.form.validation.city.required'))
        .min(
          TEXT_LENGTH_LIMITS.MIN,
          t('clients.form.validation.city.minLength', '', { min: TEXT_LENGTH_LIMITS.MIN }),
        )
        .max(
          TEXT_LENGTH_LIMITS.MAX,
          t('clients.form.validation.city.maxLength', '', { max: TEXT_LENGTH_LIMITS.MAX }),
        ),
    }),
    onSubmit: async (values) => {
      await createLocation(values);
      navigate(ROUTE.LOCATIONS);
    },
  });

  const updateFormikUnits = (isCelsius: boolean) => {
    formik.setFieldValue('isCelsius', isCelsius);
  };

  const onUnitsChange = (data: ResponseSwitch) => {
    updateFormikUnits(data.value);
  };

  const isDisabledBtn = Boolean(
    (formik.touched.name && formik.errors.name) || (formik.touched.city && formik.errors.city),
  );

  const fetchDataHandler = async () => {
    try {
      setLoading(true);
      const units = await getOrganizationUnits();
      updateFormikUnits(units === CycleMeasurement.Celsius);
    } catch (error) {
      console.error('Error fetching organization units:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataHandler();
  }, []);

  if (loading) {
    return (
      <div className="c-create-location bg-white ">
        <div className="flex w-full items-center justify-center rounded-sm border border-stroke p-4 shadow-default dark:border-strokedark dark:bg-boxdark">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="p-create-location pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">{t('clients.form.heads.create')}</h3>
        </div>
        <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
          <div className="w-full">
            <InputControl
              label={t('clients.form.inputs.name.label')}
              type="text"
              name="name"
              placeholder={t('clients.form.inputs.name.placeholder')}
              value={formik.values.name}
              customOnChange={formik.handleChange}
              onBlur={formik.handleBlur}
              showError={formik.touched.name && formik.errors.name}
              errorText={formik.errors.name}
            />
          </div>
          <div className="w-full">
            <InputControl
              label={t('clients.form.inputs.city.label')}
              type="text"
              name="city"
              placeholder={t('clients.form.inputs.city.placeholder')}
              value={formik.values.city}
              customOnChange={formik.handleChange}
              onBlur={formik.handleBlur}
              showError={formik.touched.city && formik.errors.city}
              errorText={formik.errors.city}
            />
          </div>
          <div className="c-input-control__input-wrapper">
            <p className="flex justify-between py-2.5 font-medium text-black dark:text-white">
              {t('clients.form.inputs.units.label')}
            </p>
            <Switcher
              options={{
                left: CycleMeasurementSymbol.Celsius,
                right: CycleMeasurementSymbol.Fahrenheit,
              }}
              isEnable={formik.values.isCelsius}
              onChange={onUnitsChange}
              switcherId={`${formik.values.name}-org-units-id`}
            />
            <div className="flex min-h-6 items-center justify-start"></div>
          </div>
          <div className="flex justify-end gap-4.5">
            <InteractiveButton
              text={t('buttons.create')}
              submit
              disabled={isDisabledBtn}
              isLoading={formik.isSubmitting}
            />
          </div>
        </form>
      </div>
      <DraftMetaFields
        metaFields={formik.values.metaFields}
        onChange={(list) => formik.setFieldValue('metaFields', list)}
        title={t('labels.locationMetaFields')}
      />
    </div>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  createLocation: dispatch.locations.createLocation,
  getOrganizationUnits: dispatch.organizations.getOrganizationUnits,
});

export default connect(null, mapDispatch)(CreateLocation);
